import type { Feature, LineString, Polygon, Point } from 'geojson'

import type {
  CreateUpdateShapeInput,
  CreateUpdateLineInput,
  CreateUpdateWaypointInput,
  Line,
  Track,
  Shape,
  Route,
  Waypoint,
} from '@onxmaps/markups-web'

/**
 * Enum representing the different types of markups that can be used in the application.
 *
 * @enum {string}
 * @property {string} waypoint - Represents a single point of interest or location.
 * @property {string} line - Represents a straight or polyline segment.
 * @property {string} track - Represents a recorded or planned path.
 * @property {string} shape - Represents a geometric shape, such as a polygon.
 * @property {string} route - Represents a navigational route consisting of multiple segments.
 */
export enum MarkupType {
  waypoint = 'waypoint',
  line = 'line',
  track = 'track',
  shape = 'shape',
  route = 'route',
}

/**
 * Base interface for raw markup objects (JSON) retrieved from the markup REST API.
 *
 * @interface RawMarkup
 */
export interface RawMarkup {
  /** Optional application that created this markup */
  app?: string
  /** Optional attachments containing photos */
  attachments?: { photos: MarkupPhoto[] }
  /** Optional RGB color array for display */
  color?: number[]
  /** ISO timestamp when created */
  created_at: string
  /** Markup geometry */
  geo_json: Feature<Point | Polygon | LineString>
  /** Optional whether the markup is currently shared */
  has_active_shares?: boolean
  /** Optional ISO timestamp of last sync */
  last_synced_at?: string
  /** Display name */
  name: string
  /** Optional notes / description */
  notes: string | null
  /** Optional owner information */
  owner?: {
    /** Owner account ID */
    account_id: string
    /** Owner first name */
    first_name: string
    /** Owner last name */
    last_name: string
    /** Owner full display name */
    name: string
    /** URL to owner photo */
    photo_url: string
  }
  /** Optional permission strings */
  permissions?: string[]
  /** Markup category/type */
  type: MarkupType
  /** ISO timestamp when last updated */
  updated_at: string
  /** Unique identifier */
  uuid: string
}
/**
 * Interface defining the shared properties of any markup view modal.
 *
 * @interface MarkupProperties
 */
export interface MarkupProperties {
  /** Indicates whether the markup is valid */
  validMarkup: boolean
  /** Model for the collection list */
  collectionListModel: unknown
  /** Array of collection IDs that this markup was added to */
  collectionsAddedTo: string[]
  /** Array of collection IDs that this markup was removed from */
  collectionsRemovedFrom: string[]
  /** The name of the markup */
  name: string
  /** Optional application identifier */
  app?: string
  /** Additional notes for the markup, can be null */
  notes: string | null
  /** Unique identifier for the markup */
  uuid: string
  /** Optional owner information object */
  owner?: {
    /** Owner's account identifier */
    account_id: string
    /** Owner's first name */
    first_name: string
    /** Owner's last name */
    last_name: string
    /** Owner's full name */
    name: string
    /** URL to owner's photo */
    photo_url: string
  }
  /** Optional owner name string */
  ownerName?: string
  /** Optional owner photo URL */
  ownerPhoto?: string
  /** Optional array of permission strings */
  permissions?: string[]
  /** Optional flag indicating if the markup is shared */
  isShared?: boolean
  /** Optional flag indicating if there are active shares */
  hasActiveShares?: boolean
  /** Optional flag indicating if the markup can be copied */
  canBeCopied?: boolean
  /** Timestamp when the markup was created */
  createdAt: Date
  /** Timestamp when the markup was last updated */
  updatedAt: Date
}

/**
 * Represents a photo attached to a markup with metadata and upload information.
 *
 * @interface MarkupPhoto
 */
export interface MarkupPhoto {
  /** The application identifier */
  app: string
  /** Client-side creation timestamp */
  client_created_at: string
  /** Client-side last update timestamp */
  client_updated_at: string
  /** Identifier of the user who created the photo */
  created_by: string
  /** Server-side insertion timestamp */
  inserted_at: string
  /** Public identifier for the photo */
  public_id: string
  /** Optional URL for the photo thumbnail */
  thumbnail_url?: string
  /** Server-side last update timestamp */
  updated_at: string
  /** Flag indicating whether the photo has been uploaded */
  uploaded: boolean
  /** Optional URL for the full-size photo */
  url?: string
  /** Unique identifier for the photo */
  uuid: string
}

/**
 * Input interface for creating or updating a markup.
 *
 * @interface CreateUpdateMarkupInput
 */
export interface CreateUpdateMarkupInput {
  /** Optional application identifier associated with the markup */
  application?: string
  /** Optional attachment configuration for the markup */
  attachments?: {
    /** Photo attachment operations */
    photos: {
      /** Array of photo UUIDs to add as attachments */
      add: { uuid: string }[]
      /** Array of photo UUIDs to remove from attachments */
      remove: { uuid: string }[]
    }
  }
  /** Optional color specification as RGB array or color string */
  color?: number[] | string
  /** ISO timestamp when the markup was created */
  created_at: string
  /** GeoJSON feature defining the markup's geometry */
  geo_json: Feature<Point | Polygon | LineString>
  /** Display name for the markup */
  name: string
  /** Optional notes or description for the markup */
  notes: string | null
  /** Classification type of the markup */
  type: MarkupType
  /** ISO timestamp when the markup was last updated */
  updated_at: string
  /** Unique identifier for the markup */
  uuid: string
}

/**
 * Represents a markup view modal that can be displayed on the map.
 * This is a union type that encompasses all possible markup types including
 * waypoints, shapes, lines, and routes.
 */
export type Markup = Line | Track | Shape | Route | Waypoint

/**
 * Input type for creating or updating a markup from the markups package.
 * This is a union type that currently only includes shape and line markups.
 */
export type PackageCreateUpdateMarkupInput =
  | CreateUpdateLineInput
  | CreateUpdateShapeInput
  | CreateUpdateWaypointInput

// =============================================================================
// ROUTES
// =============================================================================

/**
 * Input interface for creating a route markup.
 *
 * @interface RawRoute
 */
export interface RawRoute {
  /** The color of the route markup */
  color: string
  /** Optional timestamp when the markup was created */
  createdAt?: string
  /** Optional identifier of who created the markup */
  createdBy?: string
  /** Whether the markup is marked as deleted */
  deleted: boolean
  /** Optional unique identifier for the markup */
  id?: string
  /** Timestamp when the markup was last modified */
  lastModifiedAt: string
  /** Display name of the route markup */
  name: string
  /** Optional notes or description for the markup */
  notes: string | null
  /** Optional array of permission identifiers */
  permissions?: string[]
  /** Route data containing geometry and navigation information */
  route: {
    /** Bounding box coordinates for the route */
    bounds: number[][]
    /** Total distance of the route */
    distance: number
    /** Total duration to complete the route */
    duration: number
    /** Encoded geometry string representing the route path */
    geometry: string
    /** Array of route segments */
    legs: {
      /** Type of activity for this leg */
      activity: string
      /** Distance of this route leg */
      distance: number
      /** Duration of this route leg */
      duration: number
      /** Steps data (currently null) */
      steps: null
      /** Waypoints for this route leg */
      waypoints: {
        /** Coordinate location of the waypoint */
        location: number[]
        /** Radius for snapping to the waypoint */
        snapRadius: number
      }[]
    }[]
    /** Configuration options for route calculation */
    options: {
      /** Whether to allow unsnapped waypoints */
      freeUnsnappedWaypoints: boolean
      /** Whether to constrain route to a cluster */
      constrainToCluster: boolean
    }
    /** Name of the tileset used for routing */
    tilesetName: string
    /** Version of the tileset used for routing */
    tilesetVersion: number
  }
  /** Visual style identifier for the route markup */
  style: string
  /** Line weight/thickness for displaying the route */
  weight: string
}
/**
 * Input interface for updating route markup properties.
 *
 * @interface UpdateRouteInput
 */
export interface UpdateRouteInput {
  /** Optional route configuration object */
  route?: {
    /** Bounding box coordinates for the route */
    bounds: number[][]
    /** Total distance of the route */
    distance: number
    /** Total duration of the route */
    duration: number
    /** Encoded geometry string representing the route path */
    geometry: string
    /** Array of route leg segments */
    legs: {
      /** Activity type for the leg segment */
      activity: string
      /** Distance of the leg segment */
      distance: number
      /** Duration of the leg segment */
      duration: number
      /** Steps data (currently null) */
      steps: null
      /** Waypoints within the leg segment */
      waypoints: {
        /** Coordinate location of the waypoint */
        location: number[]
        /** Snapping radius for the waypoint */
        snapRadius: number
      }[]
    }[]
    /** Route calculation options */
    options: {
      /** Whether to allow unsnapped waypoints */
      freeUnsnappedWaypoints: boolean
      /** Whether to constrain route to cluster */
      constrainToCluster: boolean
    }
  }
  /** Optional name for the route markup */
  name?: string
  /** Optional notes for the route markup */
  notes?: string
  /** Optional color for the route markup */
  color?: string
  /** Optional style for the route markup */
  style?: string
  /** Optional weight/thickness for the route markup */
  weight?: number
}
